import * as XLSX from "xlsx";
import { getErpBranchDetails } from "../erpClient";
import { getSessionBranchId } from "./sessionBranch";
import { getTenantNameUpper } from "./tenantName";

const GST_STATE_CODES = {
  "jammu and kashmir": "01",
  "jammu & kashmir": "01",
  "himachal pradesh": "02",
  punjab: "03",
  chandigarh: "04",
  uttarakhand: "05",
  haryana: "06",
  delhi: "07",
  "new delhi": "07",
  rajasthan: "08",
  "uttar pradesh": "09",
  bihar: "10",
  sikkim: "11",
  "arunachal pradesh": "12",
  nagaland: "13",
  manipur: "14",
  mizoram: "15",
  tripura: "16",
  meghalaya: "17",
  assam: "18",
  "west bengal": "19",
  jharkhand: "20",
  odisha: "21",
  orissa: "21",
  chhattisgarh: "22",
  "madhya pradesh": "23",
  gujarat: "24",
  "dadra and nagar haveli and daman and diu": "26",
  "daman and diu": "25",
  "dadra and nagar haveli": "26",
  maharashtra: "27",
  karnataka: "29",
  goa: "30",
  lakshadweep: "31",
  kerala: "32",
  "tamil nadu": "33",
  puducherry: "34",
  pondicherry: "34",
  "andaman and nicobar islands": "35",
  telangana: "36",
  "andhra pradesh": "37",
  ladakh: "38",
};

const GST_ISO_TO_CODE = {
  JK: "01",
  HP: "02",
  PB: "03",
  CH: "04",
  UT: "05",
  UK: "05",
  HR: "06",
  DL: "07",
  RJ: "08",
  UP: "09",
  BR: "10",
  SK: "11",
  AR: "12",
  NL: "13",
  MN: "14",
  MZ: "15",
  TR: "16",
  ML: "17",
  AS: "18",
  WB: "19",
  JH: "20",
  OD: "21",
  OR: "21",
  CG: "22",
  MP: "23",
  GJ: "24",
  DN: "26",
  DD: "25",
  MH: "27",
  KA: "29",
  GA: "30",
  LD: "31",
  KL: "32",
  TN: "33",
  PY: "34",
  AN: "35",
  TS: "36",
  TG: "36",
  AP: "37",
  LA: "38",
};

const num = (v, fallback = 0) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : fallback;
};

const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

export function formatGstDocDate(value) {
  if (value == null || value === "") return "";
  const raw = String(value).trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) return raw;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) {
    const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
    return raw;
  }
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

export function gstJsonFileStamp(docDate) {
  const formatted = formatGstDocDate(docDate);
  return formatted.replace(/\//g, "");
}

function extractPin(text) {
  const m = String(text || "").match(/\b(\d{6})\b/);
  return m ? Number(m[1]) : 0;
}

function digitsOnly(value) {
  return String(value || "").replace(/\D/g, "");
}

function gstinStateCode(gstin) {
  const g = String(gstin || "").trim().toUpperCase();
  if (/^[0-9]{2}/.test(g)) return g.slice(0, 2);
  return "";
}

export function resolveGstStateCode(state, gstin) {
  const fromGstin = gstinStateCode(gstin);
  if (fromGstin) return fromGstin;
  const raw = String(state || "").trim();
  if (!raw || raw === "0") return "24";
  if (/^\d{2}$/.test(raw)) return raw;
  const iso = raw.toUpperCase();
  if (GST_ISO_TO_CODE[iso]) return GST_ISO_TO_CODE[iso];
  const lower = raw.toLowerCase();
  if (GST_STATE_CODES[lower]) return GST_STATE_CODES[lower];
  const hit = Object.keys(GST_STATE_CODES).find((name) =>
    lower.includes(name),
  );
  return hit ? GST_STATE_CODES[hit] : "24";
}

export function getSellerBranchDetails() {
  const branches = getErpBranchDetails();
  const sessionBranchId = getSessionBranchId();
  const branch =
    branches.find((b) => Number(b?.id) === Number(sessionBranchId)) ||
    branches[0] ||
    {};
  const gstin = String(
    branch.gst_number || branch.gstin || branch.GST_number || "",
  ).trim();
  const address = String(branch.address || "").trim();
  const city =
    String(branch.city || branch.loc || "").trim() ||
    (address.split(",").slice(-1)[0] || "").replace(/\d/g, "").trim() ||
    "AHMEDABAD";
  return {
    Gstin: gstin,
    LglNm: getTenantNameUpper() || String(branch.name || "").toUpperCase(),
    Addr1: address || "-",
    Loc: city.toUpperCase(),
    Pin: extractPin(address) || Number(branch.pincode || branch.pin || 0) || 0,
    Stcd: resolveGstStateCode(branch.state, gstin),
    Ph: digitsOnly(branch.mobile || branch.phone || branch.ph || ""),
  };
}

function itemTaxableAmount(item) {
  return round2(
    item.total_amount ??
      item.totalamt ??
      item.item_amount ??
      item.itamt ??
      item.amount ??
      0,
  );
}

function itemQtyGrams(item) {
  const nwt = num(item.nwt ?? item.netWeight ?? item.net_weight, 0);
  if (nwt > 0) return round2(nwt);
  const gwt = num(item.gwt ?? item.grossWeight, 0);
  if (gwt > 0) return round2(gwt);
  const pcs = num(item.pcs, 0);
  return pcs > 0 ? pcs : 1;
}

function itemDescription(item, qty) {
  const name = String(
    item.item_category_name ||
      item.productGroupName ||
      item.product_group_name ||
      item.design ||
      item.name ||
      "JEWELLERY",
  )
    .trim()
    .toUpperCase();
  const qtyLabel = Number.isInteger(qty) ? String(qty) : String(qty);
  return `${name} ${qtyLabel} GRAM`.replace(/\s+/g, " ").trim();
}

function isIntraState(sellerStcd, buyerStcd) {
  return String(sellerStcd) === String(buyerStcd);
}

/**
 * Build NIC e-invoice schema 1.1 JSON objects from enriched sale rows
 * (same shape Invoice.jsx uses after `buildInvoiceWithDetails`).
 */
export function buildGstEInvoiceJson(invoices = []) {
  const seller = getSellerBranchDetails();
  return invoices.map((invoice) => {
    const customer = invoice.customerDetails || {};
    const userss = invoice.userss || {};
    const buyerGstin = String(
      customer.gst_number ||
        customer.GST_number ||
        invoice.customer_gst_number ||
        userss.GST_number ||
        "",
    ).trim();
    const buyerName = String(
      customer.company_name ||
        customer.name ||
        invoice.customerName ||
        invoice.name ||
        userss.name ||
        "",
    ).trim();
    const buyerAddr = String(
      customer.company_address ||
        customer.address ||
        invoice.customer_address ||
        invoice.address ||
        userss.address ||
        "",
    ).trim();
    const buyerCity = String(
      customer.city || invoice.customer_city || invoice.city || userss.city || "",
    )
      .trim()
      .toUpperCase();
    const buyerState = customer.state || invoice.state || userss.state || "";
    const buyerStcd = resolveGstStateCode(buyerState, buyerGstin);
    const buyerPhone = digitsOnly(
      customer.mobile || invoice.customerMobile || invoice.mobile || userss.mobile,
    );
    const intra = isIntraState(seller.Stcd, buyerStcd);
    const items = Array.isArray(invoice.items)
      ? invoice.items
      : Array.isArray(invoice.sales)
        ? invoice.sales
        : [];
    const gstRate = num(invoice.gst_percent, 3);
    let assVal = items.reduce((sum, it) => sum + itemTaxableAmount(it), 0);
    if (assVal <= 0) {
      assVal = round2(
        invoice.final_amount ?? invoice.total_amount ?? invoice.subtotal ?? 0,
      );
    }
    const taxAmount = round2(
      invoice.gst_amount ?? invoice.tax_amount ?? (assVal * gstRate) / 100,
    );
    let cgstVal = round2(invoice.cgst);
    let sgstVal = round2(invoice.sgst);
    let igstVal = round2(invoice.igst);
    if (cgstVal + sgstVal + igstVal <= 0 && taxAmount > 0) {
      if (intra) {
        cgstVal = round2(taxAmount / 2);
        sgstVal = round2(taxAmount - cgstVal);
      } else {
        igstVal = taxAmount;
      }
    }
    const rndOff = round2(invoice.round_off);
    const totInv = round2(
      invoice.grandTotal ?? invoice.grand_total ?? assVal + taxAmount + rndOff,
    );
    const itemList = (items.length ? items : [{}]).map((item, idx) => {
      const qty = itemQtyGrams(item);
      const assAmt = itemTaxableAmount(item) || (idx === 0 ? assVal : 0);
      const share = assVal > 0 ? assAmt / assVal : 1 / Math.max(items.length, 1);
      const cgstAmt = round2(cgstVal * share);
      const sgstAmt = round2(sgstVal * share);
      const igstAmt = round2(igstVal * share);
      const unitPrice = qty > 0 ? round2(assAmt / qty) : assAmt;
      const totItemVal = round2(assAmt + cgstAmt + sgstAmt + igstAmt);
      return {
        SlNo: String(idx + 1),
        PrdDesc: itemDescription(item, qty),
        IsServc: "N",
        HsnCd: String(item.hsn || item.HSN_code || item.hsn_code || "711319"),
        Qty: qty,
        Unit: "GMS",
        UnitPrice: unitPrice,
        TotAmt: assAmt,
        AssAmt: assAmt,
        GstRt: gstRate,
        IgstAmt: igstAmt,
        CgstAmt: cgstAmt,
        SgstAmt: sgstAmt,
        CesRt: 0,
        CesAmt: 0,
        TotItemVal: totItemVal,
      };
    });

    const valDtls = {
      AssVal: assVal,
      CgstVal: cgstVal,
      SgstVal: sgstVal,
      RndOffAmt: rndOff,
      TotInvVal: totInv,
    };
    if (igstVal > 0) valDtls.IgstVal = igstVal;

    const buyer = {
      Gstin: buyerGstin || "URP",
      LglNm: buyerName || "CUSTOMER",
      Pos: buyerStcd,
      Addr1: buyerAddr || "-",
      Loc: buyerCity || "NA",
      Pin: extractPin(buyerAddr) || 0,
      Stcd: buyerStcd,
    };
    if (buyerPhone) buyer.Ph = buyerPhone;

    const sellerOut = { ...seller };
    if (!sellerOut.Ph) delete sellerOut.Ph;

    return {
      Version: "1.1",
      TranDtls: {
        TaxSch: "GST",
        SupTyp: buyerGstin ? "B2B" : "B2C",
        IgstOnIntra: intra && igstVal > 0 ? "Y" : "N",
        RegRev: "N",
      },
      DocDtls: {
        Typ: "INV",
        No: String(
          invoice.voucherNumber ||
            invoice.doc_number ||
            invoice.voucher_number ||
            invoice.invoiceNumber ||
            "",
        ),
        Dt: formatGstDocDate(
          invoice.creationDate || invoice.doc_date || invoice.date,
        ),
      },
      SellerDtls: sellerOut,
      BuyerDtls: buyer,
      ValDtls: valDtls,
      ItemList: itemList,
    };
  });
}

export function downloadGstEInvoiceFile(payload, invoices) {
  const first = payload[0] || {};
  const docNo = first?.DocDtls?.No || "invoice";
  const stamp =
    gstJsonFileStamp(first?.DocDtls?.Dt) ||
    gstJsonFileStamp(invoices?.[0]?.creationDate) ||
    gstJsonFileStamp(new Date());
  const filename = `${docNo}_${stamp}eInvoice.json`;
  const blob = new Blob([JSON.stringify(payload, null, 4)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  return filename;
}

function normalizeHeader(h) {
  return String(h || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

const HEADER_MAP = {
  "sl no": "sl_no",
  irn: "irn",
  "ack no": "ack_no",
  "ack date": "ack_date",
  "doc no": "doc_no",
  "doc typ": "doc_typ",
  "doc type": "doc_typ",
  "doc date": "doc_date",
  "inv value": "inv_value",
  "inv value.": "inv_value",
  "recipient gstin": "recipient_gstin",
  status: "einvoice_status",
  "signed qr code": "signed_qr",
  "ewb no if any errors while creating ewb": "ewb_no",
  "ewb no if any errors while creating ewb.": "ewb_no",
};

export async function parseGstPortalExcel(file) {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array" });
  const sheetName = workbook.SheetNames?.[0];
  if (!sheetName) return [];
  const sheet = workbook.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    defval: "",
    raw: false,
    blankrows: false,
  });
  if (!rows.length) return [];
  const headers = (rows[0] || []).map((h) => HEADER_MAP[normalizeHeader(h)] || "");
  return rows
    .slice(1)
    .map((cells) => {
      const row = {};
      headers.forEach((key, i) => {
        if (!key) return;
        const value = cells[i];
        row[key] = value == null ? "" : String(value).trim();
      });
      return row;
    })
    .filter((row) => row.doc_no || row.irn);
}

export function mapExcelRowToEinvoicePayload(row, saleId) {
  const blankToNull = (v) => {
    if (v == null) return null;
    const s = String(v).trim();
    return s === "" ? null : s;
  };
  const invoiceValue =
    row.inv_value != null && row.inv_value !== ""
      ? num(row.inv_value)
      : row.invoice_value != null && row.invoice_value !== ""
        ? num(row.invoice_value)
        : null;

  return {
    sale_id: saleId != null ? Number(saleId) : null,
    doc_number: row.doc_no || row.doc_number || "",
    irn: row.irn || "",
    ack_no: row.ack_no || "",
    ack_date: row.ack_date || "",
    doc_type_code: row.doc_typ || row.doc_type_code || "",
    doc_date: row.doc_date || "",
    invoice_value: invoiceValue,
    recipient_gstin: row.recipient_gstin || "",
    portal_status: row.einvoice_status || row.portal_status || "",
    signed_qr_code: row.signed_qr || row.signed_qr_code || "",
    ewb_no: blankToNull(row.ewb_no),
    ewb_error: blankToNull(row.ewb_error),
  };
}

export {
  formatAckDateDisplay,
  resolveSaleEinvoiceFields,
  buildSignedQrDataUrl,
  dataUrlToPdfImageSource,
} from "./einvoiceFields";
